import { Component, OnInit, ChangeDetectorRef, inject } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule, AbstractControl, ValidationErrors } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { AuthService } from '../service/auth/auth.service';
import { RestService } from '../service/rest/rest.service';
import { LoggerService } from '../service/utils/logger.service';
import { BrandService } from '../service/brand/brand.service';

interface UserProfile {
  id: number;
  username: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneCountryPrefix: string;
  phoneNumber: string;
  country: string;
  authority: string;
  countryId?: number;
  authorityId?: number;
  countryCode?: string;
  authorityCode?: string;
  acceptMarketing: boolean;
  createdAt: string;
  externalProviderId?: number;
  deletionRequestedAt?: string;
  deletionScheduledAt?: string;
  deletionCancelledAt?: string;
}

interface Country {
  id: number;
  code: string;
  name: string;
}

interface Authority {
  id: number;
  name: string;
  countryId: number;
  countryCode: string;
  countryName: string;
}

interface PhoneCountry {
  code: string;
  name: string;
  flag: string;
  prefix: string;
}

@Component({
  selector: 'app-my-profile',
  standalone: true,
  imports: [RouterLink, ReactiveFormsModule, FormsModule, CommonModule],
  templateUrl: './my-profile.component.html',
  styleUrl: './my-profile.component.css'
})
export class MyProfileComponent implements OnInit {
  protected readonly brand = inject(BrandService);
  activeTab: 'profile' | 'password' | 'data' | 'delete' = 'profile';
  profileForm!: FormGroup;
  passwordForm!: FormGroup;
  userProfile: UserProfile | null = null;
  isLoading = false;
  isSaving = false;
  isChangingPassword = false;
  isDeleting = false;
  isCancellingDeletion = false;
  isRequestingExport = false;
  exportMessage: { type: 'success' | 'error', text: string } | null = null;
  showDeleteConfirm1 = false;
  showDeleteConfirm2 = false;
  passwordStrength = 0;
  passwordStrengthLabel = '';
  passwordStrengthColor = '';
  passwordError: string | null = null;
  profileMessage: { type: 'success' | 'error', text: string } | null = null;
  passwordMessage: { type: 'success' | 'error', text: string } | null = null;
  isExternalUser: boolean = false;
  usesSupabaseAuth = false;
  completionMode = false;
  profileComplete = false;
  returnTo = '/dashboard';
  countries: Country[] = [];
  authorities: Authority[] = [];
  filteredAuthorities: Authority[] = [];

  // Common country codes with flags (emoji flags) for phone prefix
  phoneCountries: PhoneCountry[] = [
    { code: 'US', name: 'United States', flag: '🇺🇸', prefix: '+1' },
    { code: 'GB', name: 'United Kingdom', flag: '🇬🇧', prefix: '+44' },
    { code: 'DE', name: 'Germany', flag: '🇩🇪', prefix: '+49' },
    { code: 'FR', name: 'France', flag: '🇫🇷', prefix: '+33' },
    { code: 'IT', name: 'Italy', flag: '🇮🇹', prefix: '+39' },
    { code: 'ES', name: 'Spain', flag: '🇪🇸', prefix: '+34' },
    { code: 'NL', name: 'Netherlands', flag: '🇳🇱', prefix: '+31' },
    { code: 'BE', name: 'Belgium', flag: '🇧🇪', prefix: '+32' },
    { code: 'CH', name: 'Switzerland', flag: '🇨🇭', prefix: '+41' },
    { code: 'AT', name: 'Austria', flag: '🇦🇹', prefix: '+43' },
    { code: 'SE', name: 'Sweden', flag: '🇸🇪', prefix: '+46' },
    { code: 'NO', name: 'Norway', flag: '🇳🇴', prefix: '+47' },
    { code: 'DK', name: 'Denmark', flag: '🇩🇰', prefix: '+45' },
    { code: 'FI', name: 'Finland', flag: '🇫🇮', prefix: '+358' },
    { code: 'PL', name: 'Poland', flag: '🇵🇱', prefix: '+48' },
    { code: 'CZ', name: 'Czech Republic', flag: '🇨🇿', prefix: '+420' },
    { code: 'GR', name: 'Greece', flag: '🇬🇷', prefix: '+30' },
    { code: 'PT', name: 'Portugal', flag: '🇵🇹', prefix: '+351' },
    { code: 'IE', name: 'Ireland', flag: '🇮🇪', prefix: '+353' },
    { code: 'AU', name: 'Australia', flag: '🇦🇺', prefix: '+61' },
    { code: 'CA', name: 'Canada', flag: '🇨🇦', prefix: '+1' },
    { code: 'NZ', name: 'New Zealand', flag: '🇳🇿', prefix: '+64' },
    { code: 'JP', name: 'Japan', flag: '🇯🇵', prefix: '+81' },
    { code: 'CN', name: 'China', flag: '🇨🇳', prefix: '+86' },
    { code: 'IN', name: 'India', flag: '🇮🇳', prefix: '+91' },
    { code: 'BR', name: 'Brazil', flag: '🇧🇷', prefix: '+55' },
    { code: 'MX', name: 'Mexico', flag: '🇲🇽', prefix: '+52' },
    { code: 'AE', name: 'United Arab Emirates', flag: '🇦🇪', prefix: '+971' },
    { code: 'SA', name: 'Saudi Arabia', flag: '🇸🇦', prefix: '+966' },
    { code: 'ZA', name: 'South Africa', flag: '🇿🇦', prefix: '+27' },
  ];

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private restService: RestService,
    private router: Router,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef
  ) {
    this.initForms();
  }

  ngOnInit() {
    this.completionMode = this.route.snapshot.queryParamMap.get('complete') === '1';
    this.returnTo = this.route.snapshot.queryParamMap.get('returnTo') || '/dashboard';
    if (this.completionMode) {
      this.activeTab = 'profile';
    }
    // Load user profile (which will load countries/authorities first)
    this.loadUserProfile();
  }

  async loadCountries() {
    try {
      const user = this.authService.getUser();
      if (!user || !user.sid) {
        return;
      }
      const countries = await this.restService.getCountries(user.sid);
      if (countries) {
        this.countries = countries;
      }
    } catch (error) {
      LoggerService.error('Error loading countries:', error);
    }
  }

  async loadAuthorities(countryId?: number) {
    try {
      const user = this.authService.getUser();
      if (!user || !user.sid) {
        return;
      }
      // Load all authorities regardless of country
      const authorities = await this.restService.getAuthorities(user.sid);
      if (authorities) {
        // Sort by country name, then by authority name
        const sorted = authorities.sort((a, b) => {
          const countryCompare = (a.countryName || '').localeCompare(b.countryName || '');
          if (countryCompare !== 0) return countryCompare;
          return (a.name || '').localeCompare(b.name || '');
        });
        this.authorities = sorted;
        this.filteredAuthorities = sorted;
      }
    } catch (error) {
      LoggerService.error('Error loading authorities:', error);
    }
  }

  async onCountryChange() {
    // Country change no longer affects authority selection
    // Authorities are independent of country selection
  }

  initForms() {
    this.profileForm = this.fb.group({
      username: [{ value: '', disabled: true }],
      firstName: [{ value: '', disabled: true }],
      lastName: [{ value: '', disabled: true }],
      email: ['', [Validators.required, Validators.email]],
      phoneCountryPrefix: [''],
      phoneNumber: ['', [Validators.maxLength(20)]],
      country: [{ value: '', disabled: true }],  // Legacy field
      authority: [{ value: '', disabled: true }],  // Legacy field
      countryId: [null],
      authorityId: [null],
      acceptMarketing: [false],
      createdAt: [{ value: '', disabled: true }]
    });
    
    // Country and authority are now independent - no need to watch for country changes

    this.passwordForm = this.fb.group({
      currentPassword: ['', [Validators.required, Validators.maxLength(100)]],
      newPassword: ['', [Validators.required, Validators.minLength(8), Validators.maxLength(100)]],
      confirmPassword: ['', [Validators.required, Validators.maxLength(100)]]
    }, { validators: this.passwordMatchValidator });
    
    // Watch for password changes to update strength indicator
    this.passwordForm.get('newPassword')?.valueChanges.subscribe(value => {
      if (value) {
        this.calculatePasswordStrength(value);
      } else {
        this.passwordStrength = 0;
        this.passwordStrengthLabel = '';
        this.passwordStrengthColor = '';
      }
    });
    
    // Clear password error when user starts typing in current password field
    this.passwordForm.get('currentPassword')?.valueChanges.subscribe(() => {
      if (this.passwordError) {
        this.passwordError = null;
        this.passwordForm.get('currentPassword')?.setErrors(null);
      }
    });
  }


  private isLikelyJwtAccessToken(token: string | null | undefined): boolean {
    return !!token && token.split('.').length >= 3;
  }

  private updatePasswordValidators(): void {
    const currentPassword = this.passwordForm.get('currentPassword');
    if (!currentPassword) return;
    currentPassword.setValidators([Validators.required, Validators.maxLength(100)]);
    currentPassword.updateValueAndValidity({ emitEvent: false });
  }

  requiresProfileFields(): boolean {
    return this.completionMode || this.usesSupabaseAuth || this.isExternalUser;
  }

  private updateProfileValidators(): void {
    const required = this.requiresProfileFields();
    const emailControl = this.profileForm.get('email');
    emailControl?.setValidators(required ? [Validators.required, Validators.email] : [Validators.email]);
    emailControl?.updateValueAndValidity({ emitEvent: false });
    for (const name of ['firstName', 'lastName', 'countryId', 'authorityId']) {
      const control = this.profileForm.get(name);
      control?.setValidators(required ? [Validators.required] : []);
      control?.updateValueAndValidity({ emitEvent: false });
    }
  }

  passwordMatchValidator(control: AbstractControl): ValidationErrors | null {
    const newPassword = control.get('newPassword');
    const confirmPassword = control.get('confirmPassword');
    if (newPassword && confirmPassword && newPassword.value !== confirmPassword.value) {
      return { passwordMismatch: true };
    }
    return null;
  }

  calculatePasswordStrength(password: string) {
    let strength = 0;
    let label = 'Very Weak';
    let color = '#dc3545'; // red

    if (password.length >= 8) strength++;
    if (password.length >= 12) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^a-zA-Z0-9]/.test(password)) strength++;

    if (strength <= 2) {
      label = 'Weak';
      color = '#dc3545'; // red
    } else if (strength === 3) {
      label = 'Fair';
      color = '#fd7e14'; // orange
    } else if (strength === 4) {
      label = 'Good';
      color = '#ffc107'; // yellow
    } else if (strength === 5) {
      label = 'Strong';
      color = '#198754'; // green
    } else {
      label = 'Very Strong';
      color = '#0d6efd'; // blue
    }

    this.passwordStrength = (strength / 6) * 100;
    this.passwordStrengthLabel = label;
    this.passwordStrengthColor = color;
  }

  async loadUserProfile() {
    this.isLoading = true;
    try {
      const user = this.authService.getUser();
      if (!user || !user.sid) {
        LoggerService.error('User not authenticated', null);
        this.router.navigate(['/login']);
        return;
      }

      // Load countries and authorities first, before loading profile
      await Promise.all([
        this.loadCountries(),
        this.loadAuthorities()
      ]);

      const profile = await this.restService.getUserProfile(user.sid);
      LoggerService.log('[loadUserProfile] Received profile:', profile);
      if (profile) {
        // Map the response to UserProfile interface
        const userProfile: UserProfile = {
          id: profile.id || 0,
          username: profile.username || '',
          firstName: profile.firstName || '',
          lastName: profile.lastName || '',
          email: profile.email || '',
          phoneCountryPrefix: profile.phoneCountryPrefix || '',
          phoneNumber: profile.phoneNumber || '',
          country: profile.country || '',
          authority: profile.authority || '',
          acceptMarketing: profile.acceptMarketing === true || profile.acceptMarketing === 1,
          createdAt: profile.createdAt || '',
          externalProviderId: profile.externalProviderId !== null && profile.externalProviderId !== undefined ? profile.externalProviderId : undefined,
          deletionRequestedAt: profile.deletionRequestedAt || undefined,
          deletionScheduledAt: profile.deletionScheduledAt || undefined,
          deletionCancelledAt: profile.deletionCancelledAt || undefined
        };
        // Set countryId and authorityId from profile, ensuring proper type conversion
        if (profile.countryId) {
          userProfile.countryId = typeof profile.countryId === 'number' ? profile.countryId : parseInt(String(profile.countryId));
        }
        if (profile.authorityId) {
          userProfile.authorityId = typeof profile.authorityId === 'number' ? profile.authorityId : parseInt(String(profile.authorityId));
        }
        
        this.userProfile = userProfile;
        this.usesSupabaseAuth = this.isLikelyJwtAccessToken(user.sid);
        this.profileComplete = profile.profileComplete === true;
        // Check if externalProviderId is set (any non-null, non-undefined value means externally managed)
        const extProviderId = profile.externalProviderId;
        // Check if it's a valid external provider ID (not null, not undefined, not empty string, and if number, not 0)
        this.isExternalUser = extProviderId != null && 
                             extProviderId !== undefined && 
                             extProviderId !== '' && 
                             (typeof extProviderId !== 'number' || extProviderId > 0);
        LoggerService.log('[loadUserProfile] Full profile object:', profile);
        LoggerService.log('[loadUserProfile] externalProviderId check:', {
          externalProviderId: extProviderId,
          type: typeof extProviderId,
          isExternalUser: this.isExternalUser
        });
        await this.populateProfileForm(userProfile);
        this.updatePasswordValidators();
        this.updateProfileValidators();
        // Enable/disable fields based on external user status
        if (this.requiresProfileFields()) {
          // Supabase/social users complete these fields before trial/course activation.
          this.profileForm.get('firstName')?.enable({ emitEvent: false });
          this.profileForm.get('lastName')?.enable({ emitEvent: false });
          this.profileForm.get('countryId')?.enable({ emitEvent: false });
          this.profileForm.get('authorityId')?.enable({ emitEvent: false });
          if (this.isExternalUser) {
            this.passwordForm.get('currentPassword')?.disable();
            this.passwordForm.get('newPassword')?.disable();
            this.passwordForm.get('confirmPassword')?.disable();
          }
        } else {
          // Local users: keep firstName, lastName, countryId, authorityId disabled
          this.profileForm.get('firstName')?.disable({ emitEvent: false });
          this.profileForm.get('lastName')?.disable({ emitEvent: false });
          this.profileForm.get('countryId')?.disable({ emitEvent: false });
          this.profileForm.get('authorityId')?.disable({ emitEvent: false });
          // Enable password form fields
          this.passwordForm.get('currentPassword')?.enable();
          this.passwordForm.get('newPassword')?.enable();
          this.passwordForm.get('confirmPassword')?.enable();
        }
        // Update form validation after enabling/disabling fields
        this.profileForm.updateValueAndValidity();
        this.cdr.detectChanges(); // Force change detection to update UI
        // Don't clear messages here - they should persist if set by saveProfile
      } else {
        LoggerService.error('Failed to load user profile', null);
        this.profileMessage = { type: 'error', text: 'Failed to load profile. Please try again.' };
      }
    } catch (error) {
      LoggerService.error('Error loading user profile:', error);
      this.profileMessage = { type: 'error', text: 'An error occurred while loading your profile.' };
    } finally {
      this.isLoading = false;
    }
  }

  async populateProfileForm(profile: UserProfile) {
    // For disabled fields, we need to temporarily enable them to set the value
    // Store the disabled state of fields that are always disabled
    const usernameWasDisabled = this.profileForm.get('username')?.disabled;
    const firstNameWasDisabled = this.profileForm.get('firstName')?.disabled;
    const lastNameWasDisabled = this.profileForm.get('lastName')?.disabled;
    const countryWasDisabled = this.profileForm.get('country')?.disabled;
    const authorityWasDisabled = this.profileForm.get('authority')?.disabled;
    const createdAtWasDisabled = this.profileForm.get('createdAt')?.disabled;
    
    // Temporarily enable disabled fields to set their values
    if (usernameWasDisabled) this.profileForm.get('username')?.enable({ emitEvent: false });
    if (firstNameWasDisabled) this.profileForm.get('firstName')?.enable({ emitEvent: false });
    if (lastNameWasDisabled) this.profileForm.get('lastName')?.enable({ emitEvent: false });
    if (countryWasDisabled) this.profileForm.get('country')?.enable({ emitEvent: false });
    if (authorityWasDisabled) this.profileForm.get('authority')?.enable({ emitEvent: false });
    if (createdAtWasDisabled) this.profileForm.get('createdAt')?.enable({ emitEvent: false });
    
    // Ensure countries and authorities are loaded before setting dropdown values
    if (this.countries.length === 0) {
      await this.loadCountries();
    }
    if (this.filteredAuthorities.length === 0) {
      await this.loadAuthorities();
    }
    
    this.profileForm.patchValue({
      username: profile.username || '',
      firstName: profile.firstName || '',
      lastName: profile.lastName || '',
      email: profile.email || '',
      phoneCountryPrefix: profile.phoneCountryPrefix || '',
      phoneNumber: profile.phoneNumber || '',
      country: profile.country || '',
      authority: profile.authority || '',  // Display name from backend
      countryId: profile.countryId ? (typeof profile.countryId === 'number' ? profile.countryId : parseInt(String(profile.countryId))) : null,
      authorityId: profile.authorityId ? (typeof profile.authorityId === 'number' ? profile.authorityId : parseInt(String(profile.authorityId))) : null,
      acceptMarketing: profile.acceptMarketing || false,
      createdAt: profile.createdAt || ''
    }, { emitEvent: false });
    
    // Restore disabled state
    if (usernameWasDisabled) this.profileForm.get('username')?.disable({ emitEvent: false });
    if (firstNameWasDisabled) this.profileForm.get('firstName')?.disable({ emitEvent: false });
    if (lastNameWasDisabled) this.profileForm.get('lastName')?.disable({ emitEvent: false });
    if (countryWasDisabled) this.profileForm.get('country')?.disable({ emitEvent: false });
    if (authorityWasDisabled) this.profileForm.get('authority')?.disable({ emitEvent: false });
    if (createdAtWasDisabled) this.profileForm.get('createdAt')?.disable({ emitEvent: false });
  }

  async saveProfile() {
    if (this.profileForm.invalid) {
      this.profileForm.markAllAsTouched();
      return;
    }

    // Clear any previous messages
    this.profileMessage = null;

    this.isSaving = true;
    try {
      const user = this.authService.getUser();
      if (!user || !user.sid) {
        LoggerService.error('User not authenticated', null);
        this.profileMessage = { type: 'error', text: 'User not authenticated. Please log in again.' };
        return;
      }

      const formValue = this.profileForm.getRawValue();
      const updateData: any = {
        email: formValue.email,
        phoneCountryPrefix: formValue.phoneCountryPrefix,
        phoneNumber: formValue.phoneNumber,
        acceptMarketing: formValue.acceptMarketing
      };
      
      if (this.requiresProfileFields()) {
        updateData.firstName = formValue.firstName;
        updateData.lastName = formValue.lastName;
        // Use new countryId/authorityId
        if (formValue.countryId) {
          updateData.countryId = formValue.countryId;
        } else {
          updateData.countryId = null; // Clear if not set
        }
        if (formValue.authorityId) {
          updateData.authorityId = formValue.authorityId;
        } else {
          updateData.authorityId = null; // Clear if not set
        }
      }

      const success = await this.restService.updateUserProfile(user.sid, updateData);
      if (success) {
        // Set success message
        this.profileMessage = { type: 'success', text: 'Profile updated successfully!' };
        // Reload to get latest data (message will be preserved)
        await this.loadUserProfile(); // Reload to get latest data
        if (this.completionMode) {
          await this.router.navigateByUrl(this.returnTo);
          return;
        }
        // Clear message after 3 seconds
        setTimeout(() => {
          this.profileMessage = null;
        }, 3000);
      } else {
        this.profileMessage = { type: 'error', text: 'Failed to update profile. Please try again.' };
      }
    } catch (error) {
      LoggerService.error('Error updating profile:', error);
      this.profileMessage = { type: 'error', text: 'An error occurred while updating your profile.' };
    } finally {
      this.isSaving = false;
    }
  }

  async changePassword() {
    this.updatePasswordValidators();
    if (this.passwordForm.invalid) {
      this.passwordForm.markAllAsTouched();
      return;
    }

    // Clear any previous errors and messages
    this.passwordError = null;
    this.passwordMessage = null;

    this.isChangingPassword = true;
    try {
      const user = this.authService.getUser();
      if (!user || !user.sid) {
        LoggerService.error('User not authenticated', null);
        this.passwordMessage = { type: 'error', text: 'User not authenticated. Please log in again.' };
        return;
      }

      const formValue = this.passwordForm.value;
      const success = this.usesSupabaseAuth
        ? await this.restService.changeSupabasePassword(
          user.email,
          formValue.currentPassword,
          formValue.newPassword
        )
        : await this.restService.changePassword(user.sid, {
          currentPassword: formValue.currentPassword,
          newPassword: formValue.newPassword
        });

      if (success) {
        this.passwordMessage = { type: 'success', text: 'Password changed successfully!' };
        this.passwordForm.reset();
        this.passwordStrength = 0;
        this.passwordStrengthLabel = '';
        this.passwordStrengthColor = '';
        this.passwordError = null;
        // Clear message after 3 seconds
        setTimeout(() => {
          this.passwordMessage = null;
        }, 3000);
      } else {
        // Set inline error instead of alert
        this.passwordError = 'Current password is incorrect. Please try again.';
        this.passwordForm.get('currentPassword')?.setErrors({ incorrectPassword: true });
      }
    } catch (error: any) {
      LoggerService.error('Error changing password:', error);
      // Check if it's a 400 error (invalid password)
      if (error?.status === 400 || error?.error?.error || error?.code === 'CURRENT_PASSWORD_INVALID') {
        this.passwordError = error?.message || error?.error?.error || 'Current password is incorrect. Please try again.';
        this.passwordForm.get('currentPassword')?.setErrors({ incorrectPassword: true });
      } else {
        this.passwordMessage = {
          type: 'error',
          text: error instanceof Error && error.message
            ? error.message
            : 'An error occurred while changing your password.',
        };
      }
    } finally {
      this.isChangingPassword = false;
    }
  }

  showDeleteConfirmDialog1() {
    this.showDeleteConfirm1 = true;
  }

  closeDeleteConfirm1() {
    this.showDeleteConfirm1 = false;
  }

  proceedToDeleteConfirm2() {
    this.showDeleteConfirm1 = false;
    this.showDeleteConfirm2 = true;
  }

  closeDeleteConfirm2() {
    this.showDeleteConfirm2 = false;
  }

  async deleteAccount() {
    this.isDeleting = true;
    try {
      const user = this.authService.getUser();
      if (!user || !user.sid) {
        LoggerService.error('User not authenticated', null);
        return;
      }

      const result = await this.restService.deleteAccount(user.sid);
      if (result) {
        const scheduledDate = this.formatDate(result.deletionScheduledAt);
        alert(`Your account is scheduled for deletion on ${scheduledDate}. You can sign in again before then and cancel the deletion from My Profile.`);
        this.authService.logoutUser();
        this.router.navigate(['/login']);
      } else {
        alert('Failed to schedule account deletion. Please try again.');
      }
    } catch (error) {
      LoggerService.error('Error deleting account:', error);
      alert('An error occurred while deleting your account.');
    } finally {
      this.isDeleting = false;
      this.showDeleteConfirm2 = false;
    }
  }

  async cancelAccountDeletion() {
    this.isCancellingDeletion = true;
    try {
      const user = this.authService.getUser();
      if (!user || !user.sid) {
        this.router.navigate(['/login']);
        return;
      }
      const success = await this.restService.cancelAccountDeletion(user.sid);
      if (success) {
        await this.loadUserProfile();
        this.profileMessage = { type: 'success', text: 'Account deletion has been cancelled.' };
      } else {
        this.profileMessage = { type: 'error', text: 'Could not cancel account deletion. Please try again.' };
      }
    } catch (error) {
      LoggerService.error('Error cancelling account deletion:', error);
      this.profileMessage = { type: 'error', text: 'An error occurred while cancelling account deletion.' };
    } finally {
      this.isCancellingDeletion = false;
    }
  }

  async requestDataExport(): Promise<void> {
    const user = this.authService.getUser();
    if (!user?.sid) {
      await this.router.navigate(['/login']);
      return;
    }
    this.isRequestingExport = true;
    this.exportMessage = null;
    try {
      let job = await this.restService.createPrivacyExport(user.sid);
      for (let poll = 0; poll < 30 && (job.status === 'queued' || job.status === 'processing'); poll += 1) {
        await new Promise(resolve => window.setTimeout(resolve, 2000));
        job = await this.restService.getPrivacyExportStatus(user.sid, job.jobId);
      }
      if (job.status !== 'ready') {
        throw new Error(job.failureReason || `Export did not become ready (status: ${job.status}).`);
      }
      const blob = await this.restService.downloadPrivacyExport(user.sid, job.jobId);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${this.brand.config.storagePrefix}-data-export-${new Date().toISOString().slice(0, 10)}.zip`;
      link.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 0);
      this.exportMessage = {
        type: 'success',
        text: 'Your data export was downloaded as a ZIP archive.',
      };
    } catch (error) {
      LoggerService.error('Data export request failed', error);
      this.exportMessage = {
        type: 'error',
        text: this.brand.privacyEmail
          ? `Could not send the export request. Try again or email ${this.brand.privacyEmail}.`
          : 'Could not send the export request. Try again.',
      };
    } finally {
      this.isRequestingExport = false;
    }
  }

  hasPendingDeletion(): boolean {
    return !!this.userProfile?.deletionScheduledAt;
  }

  selectTab(tab: 'profile' | 'password' | 'data' | 'delete') {
    this.activeTab = tab;
    // Ensure password form fields are properly disabled/enabled when switching tabs
    if (tab === 'password' && this.isExternalUser) {
      this.passwordForm.get('currentPassword')?.disable();
      this.passwordForm.get('newPassword')?.disable();
      this.passwordForm.get('confirmPassword')?.disable();
    } else if (tab === 'password' && !this.isExternalUser) {
      this.passwordForm.get('currentPassword')?.enable();
      this.passwordForm.get('newPassword')?.enable();
      this.passwordForm.get('confirmPassword')?.enable();
    }
    // Ensure country/authority fields are properly enabled/disabled when switching to profile tab
    if (tab === 'profile') {
      if (this.requiresProfileFields()) {
        this.profileForm.get('countryId')?.enable({ emitEvent: false });
        this.profileForm.get('authorityId')?.enable({ emitEvent: false });
        this.profileForm.get('firstName')?.enable({ emitEvent: false });
        this.profileForm.get('lastName')?.enable({ emitEvent: false });
      } else {
        this.profileForm.get('countryId')?.disable({ emitEvent: false });
        this.profileForm.get('authorityId')?.disable({ emitEvent: false });
      }
    }
  }

  formatDate(dateString: string): string {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
    } catch {
      return dateString;
    }
  }
}
